import React, { useEffect, useRef, useState } from "react";
import videojs from "video.js";
import { FiVideo } from "react-icons/fi";
import { useAuth } from "../context/AuthContext";
import { apiFetch } from "../utils/apiFetch";
import { lessonPlayback } from "../utils/lessonPlayback.mjs";

const frames = new Map();
const publishing = new Set();

// Only fetch a preview for an accessible video when its row enters the viewport.
export default function ContentThumbnail({ lesson, canManage = false }) {
  const host = useRef(null);
  const { userId } = useAuth();
  const cacheKey = `${userId}:${lesson._id}:${lesson.video?.key || lesson.recording?.finalVideo?.key || ""}`;
  const [frame, setFrame] = useState(lesson.thumbnail || (!lesson.locked && frames.get(cacheKey)) || "");
  useEffect(() => {
    if (!canManage || lesson.thumbnail || !frame || publishing.has(cacheKey)) return;
    publishing.add(cacheKey);
    (async () => {
      const image = await fetch(frame).then(response => response.blob());
      const upload = await apiFetch(`${process.env.API_URL}/api/groups/thumbnail`, { method: "POST", headers: { "Content-Type": "image/jpeg" }, body: image });
      const data = await upload.json();
      if (!upload.ok) throw Error("Thumbnail upload failed");
      const saved = await apiFetch(`${process.env.API_URL}/api/classes/${lesson._id}/thumbnail`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ thumbnailToken: data.thumbnailToken }) });
      if (!saved.ok) throw Error("Thumbnail save failed");
    })().catch(() => publishing.delete(cacheKey));
  }, [canManage, frame, lesson.thumbnail, lesson._id, cacheKey]);
  const source = lessonPlayback(lesson, process.env.API_URL || "");
  useEffect(() => {
    if (lesson.locked || frame || !source.src) return;
    let player, timer, stopped = false;
    const stop = () => { clearTimeout(timer); if (player && !player.isDisposed()) player.dispose(); };
    const observer = new IntersectionObserver(entries => {
      if (!entries.some(entry => entry.isIntersecting) || stopped) return;
      observer.disconnect();
      const video = document.createElement("video");
      video.crossOrigin = "use-credentials"; video.muted = true; video.playsInline = true;
      const wrapper = document.createElement("div"); wrapper.hidden = true;
      host.current.appendChild(wrapper); wrapper.appendChild(video);
      player = videojs(video, { controls: false, preload: "auto", muted: true,
        html5: { vhs: { withCredentials: true, overrideNative: !videojs.browser.IS_SAFARI } },
        sources: [{ src: source.src, type: source.mimeType }] });
      player.one("loadeddata", () => {
        if (stopped) return;
        try {
          const tech = player.el().querySelector("video"), canvas = document.createElement("canvas");
          canvas.width = 320; canvas.height = 180;
          canvas.getContext("2d").drawImage(tech, 0, 0, 320, 180);
          const preview = canvas.toDataURL("image/jpeg", 0.7);
          frames.set(cacheKey, preview);
          if (frames.size > 60) frames.delete(frames.keys().next().value);
          setFrame(preview);
        } catch { /* Keep the video placeholder if the browser disallows capture. */ }
        stop(); wrapper.remove();
      });
      player.one("error", stop);
      timer = setTimeout(stop, 15000);
    });
    observer.observe(host.current);
    return () => { stopped = true; observer.disconnect(); stop(); };
  }, [source.src, frame, lesson.locked, cacheKey]);
  return <span ref={host} className="flex h-full w-full items-center justify-center">{frame && !lesson.locked ? <img src={frame} alt="" className="h-full w-full object-cover" /> : <FiVideo className="h-8 w-8 text-gray-500" />}</span>;
}
